import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import { useApp } from '../../hooks/useApp';
import ExportButton from '../../components/ui/ExportButton';

ModuleRegistry.registerModules([AllCommunityModule]);

// Loose RPC caller for get_receiving_queue (dynamic args including optional
// p_distributor_id for dist_rep scoping).
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

function isPast(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

interface StatusBadge {
  label: string;
  cls: string;
}

const STATUS_BADGES: Record<string, StatusBadge> = {
  invoice_received: { label: 'Invoice Received', cls: 'bg-teal-400/10 text-teal-400' },
  partially_received: { label: 'Partial', cls: 'bg-green-400/10 text-green-400' },
};

// Row shape returned by get_receiving_queue RPC.
interface ReceivingRow {
  id: string;
  po_number: string;
  distributor_name: string | null;
  status: string;
  order_date: string | null;
  expected_delivery_date: string | null;
  total_skus: number | null;
  total_units: number | null;
  total_cost: number | null;
  total_count: number;
}

// ── Receiving Queue ─────────────────────────────────────────────────────────

export default function Receiving(): JSX.Element {
  // Day 64.5 V-ROUI: viewer hides the "Receive" action affordance (navigation
  // entry to ReceivingDetail's mutation surface). dist_rep keeps it per
  // Architecture Rule allowing dist_rep on receiving. Pricing column at L332
  // stays gated on isDistRep only (Architecture Rule 4 — viewer sees pricing).
  const { isViewer, isDistRep, profileDistributorId } = useApp();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<ReceivingRow> | null>(null);
  const gridApiRef = useRef<GridApi<ReceivingRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [totalCount, setTotalCount] = useState<number>(0);

  // State ref for getRows
  const stateRef = useRef<{
    searchText: string;
    statusFilter: string;
    _isDistRep: boolean;
    _distributorId: string | null;
  }>({
    searchText,
    statusFilter,
    _isDistRep: isDistRep,
    _distributorId: profileDistributorId,
  });
  stateRef.current = {
    searchText,
    statusFilter,
    _isDistRep: isDistRep,
    _distributorId: profileDistributorId,
  };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf } = stateRef.current;

    try {
      const rpcParams: Record<string, unknown> = {
        p_search: st.trim() || null,
        p_status: sf === 'all' ? null : sf,
        p_limit: endRow - startRow,
        p_offset: startRow,
      };
      if (stateRef.current._isDistRep && stateRef.current._distributorId) {
        rpcParams.p_distributor_id = stateRef.current._distributorId;
      }
      const { data, error } = await callRpc('get_receiving_queue', rpcParams);

      if (error) throw new Error(error.message);

      const rows = (data ?? []) as ReceivingRow[];
      const count = rows.length > 0 ? Number(rows[0].total_count) : 0;
      setTotalCount(count);
      successCallback(rows, count);
    } catch (e) {
      reportDatasourceFailure(
        'Receiving',
        { rpcName: 'get_receiving_queue', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (event: GridReadyEvent<ReceivingRow>): void => {
      gridApiRef.current = event.api;
      event.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, getRowsFn]);

  // ── Cell renderers ────────────────────────────────────────

  type RowParams = ICellRendererParams<ReceivingRow>;

  const PONumberCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/purchasing/receiving/${p.data!.id}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {p.data.po_number}
        </button>
      );
    },
    [navigate]
  );

  const DistributorCell = useCallback((p: RowParams) => {
    if (!p.value) return <>—</>;
    const name = String(p.value);
    const short = name.length > 22 ? name.slice(0, 22).trim() + '\u2026' : name;
    return <span title={name}>{short}</span>;
  }, []);

  const StatusCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const badge = STATUS_BADGES[p.data.status] || {
      label: p.data.status,
      cls: 'bg-surface-4 text-ink-muted',
    };
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', badge.cls)}>
        {badge.label}
      </span>
    );
  }, []);

  const DateCell = useCallback((p: RowParams) => {
    if (!p.value) return <span className="text-ink-muted">—</span>;
    return <span className="font-mono">{fmtDate(p.value as string | null)}</span>;
  }, []);

  const ExpectedDeliveryCell = useCallback((p: RowParams) => {
    if (!p.value) return <span className="text-ink-muted">—</span>;
    const past = isPast(p.value as string | null);
    return (
      <span className={clsx('font-mono', past && 'text-danger')}>
        {fmtDate(p.value as string | null)}
      </span>
    );
  }, []);

  const ActionsCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      if (isViewer) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/purchasing/receiving/${p.data!.id}`);
          }}
          className="arbi-btn-primary text-2xs px-2 py-0.5 cursor-pointer"
        >
          Receive
        </button>
      );
    },
    [navigate, isViewer]
  );

  // ── Column definitions ────────────────────────────────────

  const dashIfEmpty = (p: ValueFormatterParams<ReceivingRow>): string =>
    p.value === null || p.value === undefined || p.value === '' ? '\u2014' : String(p.value);

  const columnDefs = useMemo<ColDef<ReceivingRow>[]>(
    () => [
      {
        headerName: 'PO Number',
        field: 'po_number',
        width: 140,
        suppressMovable: true,
        cellRenderer: PONumberCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Distributor',
        field: 'distributor_name',
        flex: 1,
        minWidth: 120,
        cellRenderer: DistributorCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 120,
        cellRenderer: StatusCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Order Date',
        field: 'order_date',
        width: 95,
        cellRenderer: DateCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
      },
      {
        headerName: 'Exp. Delivery',
        field: 'expected_delivery_date',
        width: 105,
        cellRenderer: ExpectedDeliveryCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
      },
      {
        headerName: 'SKUs',
        field: 'total_skus',
        width: 76,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Units',
        field: 'total_units',
        width: 76,
        valueFormatter: dashIfEmpty,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Total Cost',
        field: 'total_cost',
        width: 95,
        // Architecture Rule 4: dist_rep sees zero pricing/margin/strategy data.
        hide: isDistRep,
        valueFormatter: (p: ValueFormatterParams<ReceivingRow>) => fmt$(p.value as number | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: '',
        colId: '_actions',
        width: 80,
        sortable: false,
        filter: false,
        cellRenderer: ActionsCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
    ],
    [
      PONumberCell,
      DistributorCell,
      StatusCell,
      DateCell,
      ExpectedDeliveryCell,
      ActionsCell,
      isDistRep,
    ]
  );

  const defaultColDef = useMemo<ColDef<ReceivingRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback(
    (event: RowClickedEvent<ReceivingRow>): void => {
      if (!event.data) return;
      navigate(`/purchasing/receiving/${event.data.id}`);
    },
    [navigate]
  );

  const hasActiveFilters = searchText.trim() || statusFilter !== 'all';

  return (
    <div className="flex flex-col h-full -m-4">
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Receiving</h1>
            <span className="text-xs text-ink-muted">
              {totalCount > 0
                ? `${totalCount} PO${totalCount !== 1 ? 's' : ''} awaiting receiving`
                : ''}
            </span>
          </div>
          <ExportButton gridApiRef={gridApiRef} fileName="arbi-receiving" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {(
              [
                { id: 'all', label: 'All' },
                { id: 'invoice_received', label: 'Invoice Received' },
                { id: 'partially_received', label: 'Partial' },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer',
                  statusFilter === f.id
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search PO number, distributor..."
              aria-label="Search receiving queue"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {isDistRep && !profileDistributorId && (
        <div className="px-4 py-3 bg-danger/10 border-b border-danger/30 text-sm text-danger">
          Your account is not assigned to a distributor — contact your administrator.
        </div>
      )}

      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact<ReceivingRow>
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={200}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          overlayNoRowsTemplate={
            hasActiveFilters
              ? '<span style="color:var(--ink-muted);font-size:13px">No POs match your filters</span>'
              : '<span style="color:var(--ink-muted);font-size:13px">No POs awaiting receiving — POs appear here when they reach Invoice Received status</span>'
          }
        />
      </div>
    </div>
  );
}
